<template>
  <div
    class="app-file-input"
    :class="[
      appFileInputStylesComputed,
      borderColorClass()
    ]"
    @drop.prevent="handleDrop"
    @dragover.prevent="handleDragover"
    @dragleave="handleDragleave"
  >
    <label class="app-file-input__label">
      <input
        :accept="accept"
        class="app-file-input__field"
        type="file"
        @change="handleInput($event)"
      >

      <div class="app-file-input__cta">
        <AppUploadButton
          :theme="theme"
          centered
        >
          <slot name="icon" />
        </AppUploadButton>
      </div>
    </label>

    <transition name="fade">
      <div
        v-if="uploading"
        :class="[{'complete': uploadComplete}, textColorClass(400)]"
        class="upload-bar"
      >
        <div class="upload-bar__empty" />
        <div
          :style="`width: ${uploadProgress}%`"
          class="upload-bar__filled"
        />
      </div>
    </transition>

    <div
      :class="{'has-file': value, 'success-with-error': value && error}"
      class="app-file-input__file-info"
    >
      <div
        v-if="value"
        class="flex justify-center items-center"
      >
        <FontAwesomeIcon
          v-if="uploadComplete"
          icon="circle-check"
          class="w-15 h-15 mr-2"
          :class="textColorClass()"
        />
        <strong>{{ fileMessage }}</strong>
      </div>
      <span v-if="value">{{ value.name }}</span>
      <span v-else>{{ noFileChosenText }}</span>
    </div>

    <!-- Error message -->
    <div
      v-if="error"
      class="app-file-input__error"
    >
      <FontAwesomeIcon
        class="mr-5"
        icon="circle-exclamation"
      />
      <small>{{ error }}</small>
    </div>
  </div>
</template>

<script>
import theme from '@/mixins/theme'
import AppUploadButton from '@/components/common/buttons/AppUploadButton'
import navigation from '@/mixins/navigation'
import Axios from 'axios'
import 'url-polyfill'

export default {
  // Name
  name: 'AppFileInput',

  // Components
  components: {
    AppUploadButton
  },

  mixins: [theme, navigation],

  // Props
  props: {
    value: File,

    error: {
      type: String,
      default: ''
    },

    documentType: {
      type: String,
      default: ''
    },

    noFileChosenText: {
      type: String,
      default: 'No File Chosen'
    },

    required: {
      type: Boolean,
      default: false
    },

    docsStandAlone: {
      type: Boolean,
      default: false
    }
  },

  // Data
  data () {
    return {
      apiToken: process.env.VUE_APP_API_TOKEN,
      file: undefined,
      uploading: false,
      uploadProgress: 0,
      uploadComplete: false,
      dragover: false,
      uploadFailed: false,
      verifyId: process.env.VUE_APP_ENABLE_ID_VERIFICATION
    }
  },

  // Computed
  computed: {
    accept () {
      // handle known bug with facebook and instagram in-app browsers when using accept attribute
      if (this.facebookOrInstagramBrowser) return false
      return '.jpg, .jpeg, .png, .gif, .pdf'
    },

    facebookOrInstagramBrowser () {
      // https://stackoverflow.com/a/53705145/10560514
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      const browserList = [
        'FBAN',
        'FBAV',
        'Instagram'
      ]
      return browserList.some(e => userAgent.includes(e))
    },

    // used for non-documents standalone flow only
    cduid () {
      return this.$store.state.application.cduid
    },

    // used for documents standalone flow only
    oppId () {
      if (!this.docsStandAlone) return
      return this.$store.state.account.oppId
    },

    // used for documents standalone flow only
    accountId () {
      if (!this.docsStandAlone) return
      return this.$store.state.account.accountId
    },

    fileMessage () {
      if (this.uploadComplete) {
        return 'Success! File Uploaded:'
      } else if (this.uploading) {
        return 'Uploading:'
      } else {
        return 'File Selected:'
      }
    },

    fileValidationRules () {
      return {
        fileSizeValid: true,
        fileExtensionValid: true,
        fileTypeValid: true,
        fileUploading: { uploading: this.uploading },
        fileUploadSuccess: { uploadSuccess: !this.uploadFailed },
        fileRequired: this.required
      }
    },

    appFileInputStylesComputed () {
      return { 'bg-gray-50': this.dragover }
    },

    returningCustomer () {
      return this.$store.state.returningCustomer.is_in_progress
    },

    crm () {
      return this.$store.state.account.crm
    }
  },

  // Methods
  methods: {
    /**
     * Handle the input event
     * Check if the file is valid before upload
     * @param {Object} event
     * @emits input
     */
    handleInput (event) {
      if (!event.target.files[0]) return
      this.uploadComplete = false
      this.uploadFailed = false
      this.file = event.target.files[0]
      this.$emit('input', this.file)
      this.handleUploadFile()
    },

    /**
     * Validate File
     * Validates the currently selected file
     * @emits error
     */

    async validateFile () {
      await this.$nextTick()
      const isValid = await this.$_navigation_validateStep()
      return isValid.valid
    },

    /**
     * Upload file
     * Creates a new FormData object and uploads with axios
     * Track the onUploadProgress to increment the loading bar
     * @emits upload-start
     * @emits upload-success
     */
    async uploadFile () {
      this.uploading = true
      this.uploadProgress = 0
      this.$emit('upload-start')
      const data = this.createUploadFormData()
      try {
        const response = await Axios.request({
          headers: { 'Content-Type': 'x-www-form-urlencoded' },
          url: process.env.VUE_APP_API_DOCUMENTS_URL,
          method: 'POST',
          data: data,
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            ) || 0
          }
        })
        this.uploadComplete = true
        this.$emit('upload-success')
        return response
      } catch (error) {
        this.uploadFailed = true
      } finally {
        this.uploading = false
        this.validateFile()
      }
    },
    /**
     * Handler for drop event inside box
     * @param {Object} event
     * @emits input
     */
    handleDrop (event) {
      this.uploadComplete = false
      this.uploadFailed = false
      this.file = event.dataTransfer.files[0]
      this.dragover = false
      this.$emit('input', this.file)
      this.handleUploadFile()
    },

    /**
     * Handler for dragover event inside box
     */
    handleDragover () {
      this.dragover = true
    },

    /**
     * Handler for dragleave event inside box
     */
    handleDragleave () {
      this.dragover = false
    },

    /**
     * Helper Method to Begin Upload File process
     */
    async handleUploadFile () {
      this.uploadFile()
    },

    createUploadFormData () {
      let data = new FormData()
      data.append('files[]', this.file)
      data.append('Type__c', this.documentType)
      if (this.verifyId) data.append('verify_id', true)
      let source
      if (this.docsStandAlone) {
        data.append('Additional_Documents_Submitted__c', true)
        if (this.accountId) data.append('Account__c', this.accountId)
        if (this.oppId) data.append('Opportunity__c', this.oppId)
        if (!this.cduid) data.append('crm', this.crm)
        source = 'Agent Triggered'
      } else {
        data.append('cduid', this.cduid)
        source = this.returningCustomer ? 'Returning Customer' : 'Initial Application'
      }
      data.append('Source__c', source)
      return data
    }
  }

}
</script>

<style scoped lang="scss">
.app-file-input {
  @apply pt-30 relative mx-auto pb-100 border-dashed border-2 rounded-6;

  @screen sm {
    @apply pt-40 pb-90;
  }

  &__label {
    @apply cursor-pointer;
  }

  &__field {
    @apply hidden;
  }

  &__file-info {
    @apply absolute w-full;
    @apply text-center font-light bottom-[60px];

    &.has-file {
      @apply bottom-[30px];
    }

    @screen xs-only {
      &.success-with-error {
        @apply bottom-[55px];
      }
    }
  }

  &__error {
    @apply absolute text-center w-full text-red bottom-[10px];

    @screen xs-only {
      @apply bottom-[5px];
    }
  }
}

.upload-bar {
  @apply relative mt-15 ml-auto mr-auto w-240;

  &::before,
  &::after {
    @apply absolute block w-3 h-3;
    @apply rounded-full border-2 border-solid;

    z-index: 3;
    content: '';
  }

  &::before {
    @apply top-[-4px] left-[-1px];
    @apply border border-white bg-current;
  }

  &::after {
    @apply top-[-4px] right-[-1px];
    @apply bg-white border-gray-300;
  }

  &.complete::after {
    border-width: 1px;

    @apply border-white bg-current;
  }

  &__empty {
    @apply h-1 w-full;
    @apply bg-gray-300;
  }

  &__filled {
    @apply absolute h-1;
    @apply bg-current top-0;

    transform-origin: 0%;
    z-index: 2;
    will-change: width;
    transition: width 250ms ease-out;
  }
}
</style>
